import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CommonSelect from "../../../core/common/commonSelect";
import useApi from "../../../core/customhook/useApi";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Swal from "sweetalert2";

type Option = {
  value: string;
  label: string;
};

const KRAs = () => {
  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [count, setCount] = useState(1);
  const [newItem, setNewItem] = useState<any>({
    kar_id: "",
    category_id: "",
    smart_goals: "",
    target: "",
    target_achievement_date: "",
    attachment_url: null,
  });
  const [selectedItem, setSelectedItem] = useState<any>({});
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});

  const formatFinancialYear = (year: number) => {
    const nextYearShort = (year + 1).toString().slice(-2);
    return `${year}-${nextYearShort}`;
  };

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const fyStart = month >= 3 ? year : year - 1;
    return formatFinancialYear(fyStart);
  };
  const [finance_year, setFinance_year] = useState(getCurrentFinancialYear());

  const options_category: Option[] = useMemo(
    () =>
      categoryList?.map((item) => ({
        value: item.categoryId,
        label: item.category,
      })),
    [categoryList]
  );

  const columns = [
    {
      title: "Sr No.",
      dataIndex: "SrNo",
      render: (text: string, record: any, index: number) => (
        <h6 className="fw-medium fs-14 text-dark">{index + 1}</h6>
      ),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      sorter: (a: any, b: any) => a.category.localeCompare(b.category),
    },
    {
      title: "Smart Goal",
      dataIndex: "smart_goals",
      sorter: (a: any, b: any) => a.smart_goals.localeCompare(b.smart_goals),
    },
    {
      title: "Target",
      dataIndex: "target",
      sorter: (a: any, b: any) => a.target.localeCompare(b.target),
    },
    {
      title: "Target Date",
      dataIndex: "target_achievement_date",
      render: (text: string, record: any) => {
        return <p>{text}</p>;
      },
      sorter: (a: any, b: any) =>
        new Date(a.target_achievement_date).getTime() -
        new Date(b.target_achievement_date).getTime(),
    },
    {
      title: "Attachment",
      dataIndex: "attachment_url",
      render: (text: string, record: any, index: number) =>
        text ? (
          <a
            href={text}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info"
          >
            <i className="bi bi-file-earmark-pdf"></i> View
          </a>
        ) : (
          <span className="text-muted">No file</span>
        ),
      sorter: (a: any, b: any) =>
        (a.attachment_url || "").localeCompare(b.attachment_url || ""),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            data-bs-toggle="modal"
            data-bs-target="#edit_category"
            onClick={() => {
              console.log(record);
              setNewItem({
                kra_id: record.kra_id,
                category_id: record.category_id,
                smart_goals: record.smart_goals,
                target: record.target,
                target_achievement_date: record.target_achievement_date,
                attachment_url: record.attachment_url,
              });
            }}
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            to="#"
            className="me-2"
            onClick={() => {
              DeleteItem(record.kra_id);
            }}
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  const {
    data: getcategoryResponse,
    loading: getCategoryLoading,
    error: getCategoryError,
  } = useApi("/api/manager/view-category", {
    method: "POST",
    payload: {
      financial_year: finance_year,
    },
    autoCall: true,
  });

  useEffect(() => {
    setCategoryList(getcategoryResponse?.data);
  }, [getcategoryResponse]);

  const getInputData = (e: any) => {
    const { name, value, files } = e.target;
    if (name === "attachment_url") {
      setNewItem({ ...newItem, [name]: files[0] });
    } else {
      setNewItem((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const {
    data: viewKRAResponse,
    loading: viewKRALoading,
    error: viewKRAError,
  } = useApi("/api/manager/view-kra", {
    method: "POST",
    payload: {
      financial_year: finance_year,
    },
    autoCall: true,
    dependencies: [count],
  });

  useEffect(() => {
    if (viewKRAResponse?.success) {
      setAllItemList(viewKRAResponse?.data);
    }
  }, [viewKRAResponse]);

  const {
    data: addKRAResponse,
    loading: addKRALoading,
    error: addKRAError,
    callApi: addKRA,
  } = useApi("/api/manager/add-kra", {
    method: "POST",
    autoCall: false,
  });

  const AddNewItem = async (e: any) => {
    e.preventDefault();

    const formData = new FormData();

    Object.entries(newItem).forEach(([key, value]) => {
      if (key === "attachment_url" && value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === "string") {
        formData.append(key, value);
      } else if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });
    try {
      const response = await addKRA(formData);
      if (response.success) {
        Swal.fire({
          title: "Success!",
          text: response?.message || "KRA has been added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        document.getElementById("close_modal")?.click();
        setCount(count + 1);
        setNewItem({
          category_id: "",
          smart_goals: "",
          target: "",
          target_achievement_date: "",
          attachment_url: null,
        });
      }
    } catch (error) {
      console.error("Error while adding :", error);
    }
  };

  const {
    data: editKRAResponse,
    loading: editKRALoading,
    error: editKRAError,
    callApi: editKRA,
  } = useApi("/api/manager/edit-kra", {
    method: "POST",
    autoCall: false,
  });

  const UpdateItemData = async (e: any) => {
    e.preventDefault();
    const formData = new FormData();

    Object.entries(newItem).forEach(([key, value]) => {
      if (key === "attachment_url" && value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === "string") {
        formData.append(key, value);
      } else if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });
    try {
      const response = await editKRA(formData);
      if (response.success) {
        Swal.fire({
          title: "Success!",
          text: response?.message || "KRA has been added successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
        document.getElementById("edit_close_modal")?.click();
        setCount(count + 1);
      }
    } catch (error) {
      console.error("Error while updating :", error);
    }
  };

  const {
    data: deleteKRAResponse,
    loading: deleteKRALoading,
    error: deleteKRAError,
    callApi: deleteKRA,
  } = useApi("/api/manager/delete-kra", {
    method: "DELETE",
    autoCall: false,
  });

  const DeleteItem = async (ID: any) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this kra? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (confirmResult.isConfirmed) {
      try {
        const response = await deleteKRA(
          undefined,
          "/api/manager/delete-kra/" + ID
        );

        if (response.success) {
          Swal.fire({
            title: "Deleted!",
            text:
              response?.message || "KRA has been deleted successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          setCount(count + 1);
        }
      } catch (error) {
        console.error("Error while deleting:", error);
        Swal.fire({
          title: "Error!",
          text: "There was a problem deleting the category.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">View KRA</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Work & Operations</li>
                  <li className="breadcrumb-item">Goal Settings</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    View KRA
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              <div className="input-icon w-120 position-relative mb-2">
                <select
                  value={finance_year}
                  onChange={(e) => setFinance_year(e.target.value)}
                  className="form-select"
                >
                  {(() => {
                    const today = new Date();
                    const currentYear =
                      today.getMonth() >= 3
                        ? today.getFullYear()
                        : today.getFullYear() - 1;

                    return Array.from({ length: 1 }, (_, i) => {
                      const year = currentYear - i;
                      const nextYearShort = String(year + 1).slice(-2);
                      const financialYear = `${year}-${nextYearShort}`;

                      return (
                        <option key={financialYear} value={financialYear}>
                          {financialYear}
                        </option>
                      );
                    });
                  })()}
                </select>
              </div>
              <div className="mb-2">
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_category"
                  className="btn btn-primary custom_button d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add KRA
                </Link>
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>KRA List</h5>
            </div>
            <div className="card-body p-0">
              <Table
                dataSource={allItemList}
                columns={columns}
                Selection={false}
              />
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <p className="mb-0">
            © {new Date().getFullYear()} WeConnect. All rights reserved.
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Add Modal */}
      <div className="modal fade" id="add_category">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title text-capitalize">Add KRA</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="close_modal"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={AddNewItem}>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Category<span className="text-danger">*</span>
                      </label>
                      <select
                        name="category_id"
                        id=""
                        className="form-select"
                        onChange={getInputData}
                      >
                        <option value="">Select Category</option>
                        {categoryList?.map((item: any, index: any) => (
                          <option value={item.category_id}>
                            {item.category_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Smart Goal <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="smart_goals"
                        value={newItem?.smart_goals || ""}
                        onChange={getInputData}
                        className="form-control"
                        placeholder=" Enter Smart Goal...."
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Target <span className="text-danger">*</span>
                      </label>
                      <textarea
                        rows={3}
                        name="target"
                        value={newItem?.target || ""}
                        onChange={getInputData}
                        className="form-control"
                        placeholder="Enter Target...."
                      />
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Target Achievement Date{" "}
                      <sup className="text-danger">*</sup>
                    </label>
                    <div className="input-icon-end position-relative">
                      <DatePicker
                        className="form-control datetimepicker"
                        format={{ format: "DD-MM-YYYY", type: "mask" }}
                        placeholder="DD-MM-YYYY"
                        name="target_achievement_date"
                        required
                        value={
                          newItem.target_achievement_date
                            ? dayjs(
                              newItem.target_achievement_date,
                              "YYYY-MM-DD"
                            )
                            : null
                        }
                        onChange={(date) => {
                          const formattedDate = date
                            ? date.format("YYYY-MM-DD")
                            : "";
                          setNewItem((prev: any) => ({
                            ...prev,
                            target_achievement_date: formattedDate,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Attachment
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        name="attachment_url"
                        className="form-control"
                        onChange={getInputData}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  // data-bs-dismiss="modal"
                  className="btn btn-primary custom_button"
                >
                  Add KRA
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div className="modal fade" id="edit_category">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title text-capitalize">Edit KRA</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="edit_close_modal"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={UpdateItemData}>
              <div className="modal-body pb-0">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Category<span className="text-danger">*</span>
                      </label>
                      <select
                        name="category_id"
                        id=""
                        className="form-select"
                        onChange={getInputData}
                        value={newItem.category_id}
                      >
                        <option value="">Select Category</option>
                        {categoryList?.map((item: any, index: any) => (
                          <option value={item.category_id}>
                            {item.category_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Smart Goal <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="smart_goals"
                        value={newItem?.smart_goals || ""}
                        onChange={getInputData}
                        className="form-control"
                        placeholder=" Enter Smart Goal...."
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Target <span className="text-danger">*</span>
                      </label>
                      <textarea
                        rows={3}
                        name="target"
                        value={newItem?.target || ""}
                        onChange={getInputData}
                        className="form-control"
                        placeholder="Enter Target...."
                      />
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Target Achievement Date{" "}
                      <sup className="text-danger">*</sup>
                    </label>
                    <div className="input-icon-end position-relative">
                      <DatePicker
                        className="form-control datetimepicker"
                        format={{ format: "DD-MM-YYYY", type: "mask" }}
                        placeholder="DD-MM-YYYY"
                        name="target_achievement_date"
                        required
                        value={
                          newItem.target_achievement_date
                            ? dayjs(
                              newItem.target_achievement_date,
                              "YYYY-MM-DD"
                            )
                            : null
                        }
                        onChange={(date) => {
                          const formattedDate = date
                            ? date.format("YYYY-MM-DD")
                            : "";
                          setNewItem((prev: any) => ({
                            ...prev,
                            target_achievement_date: formattedDate,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Attachment
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        name="attachment_url"
                        className="form-control"
                        onChange={getInputData}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default KRAs;
